/**
 * Página pública: Avaliação da Reação do Formando (anónima, semi-cega).
 *
 * Renderiza 22 escalas 1-5 + textarea de sugestões.
 * URL: /cursos/avaliacao/:token
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/public/cursos/avaliacao`;

export default function CursoAvaliacaoPublicPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [answers, setAnswers] = useState({});
  const [sugestoes, setSugestoes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${BASE}/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || "Erro a carregar formulário");
        }
        return r.json();
      })
      .then((d) => { if (alive) setState({ loading: false, error: "", data: d }); })
      .catch((e) => { if (alive) setState({ loading: false, error: e.message, data: null }); });
    return () => { alive = false; };
  }, [token]);

  const data = state.data;

  const cursoSections = useMemo(() => {
    if (!data?.questions) return { curso: [], formador: [] };
    return {
      curso: data.questions.filter((q) => q.section === "curso"),
      formador: data.questions.filter((q) => q.section === "formador"),
    };
  }, [data]);

  const allAnswered = useMemo(() => {
    if (!data?.questions) return false;
    return data.questions.every((q) => Number.isInteger(answers[q.key]) && answers[q.key] >= 1 && answers[q.key] <= 5);
  }, [data, answers]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const payload = { ...answers, sugestoes: sugestoes.trim() || undefined };
      const r = await fetch(`${BASE}/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        const msg = typeof j.detail === "object" ? (j.detail.errors || []).join("; ") : (j.detail || "Erro a submeter");
        throw new Error(msg);
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      alert(err.message || "Erro a submeter avaliação");
    } finally {
      setSubmitting(false);
    }
  };

  // ── States ────────────────────────────────────────────────────
  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F2] flex items-center justify-center">
        <p className="text-sm text-[#6B7280] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> A carregar...</p>
      </div>
    );
  }
  if (state.error || !data) {
    return <CenterCard tone="error" title="Link inválido"
      subtitle={state.error || "Não foi possível abrir esta avaliação. Se acredita que isto é um erro, contacte ihavethepower@solfut.com."} />;
  }
  if (data.closed) {
    return <CenterCard tone="warn" title="Avaliação fechada"
      subtitle={`A avaliação do curso "${data.curso.nome_curso || ""}" já foi encerrada pela equipa IHTP. Obrigado!`} />;
  }
  if (data.already_submitted || done) {
    return <CenterCard tone="ok" title={done ? "Obrigado pela sua avaliação!" : "Já respondeu a esta avaliação"}
      subtitle={done
        ? "A sua resposta foi registada de forma anónima. O seu feedback é fundamental para continuarmos a melhorar."
        : "Esta avaliação só pode ser submetida uma vez. Se precisa alterar a sua resposta, contacte ihavethepower@solfut.com."} />;
  }

  // ── Formulário ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FAF8F2] py-8 px-3" data-testid="curso-avaliacao-public">
      <form onSubmit={onSubmit} className="max-w-3xl mx-auto bg-white border border-[#E6E1D6] rounded-md shadow-sm">
        <header className="p-5 border-b border-[#E6E1D6]">
          <p className="text-[11px] uppercase tracking-[0.15em] text-[#978D78] mb-1">Avaliação da Reação do Formando</p>
          <h1 className="text-xl font-bold text-[#0F1829]">{data.curso.nome_curso || "Curso"}</h1>
          <p className="text-xs text-[#6B7280] mt-1">
            Indique a sua opinião sobre o presente curso, marcando na escala 1-5 cada item.
            A sua resposta é <strong>anónima</strong>.
          </p>
        </header>

        <Section title="Sobre o Curso" questions={cursoSections.curso} answers={answers} setAnswers={setAnswers} />
        <Section title="Sobre o Formador" subtitle="(1 = FRACO, 5 = EXCELENTE)" questions={cursoSections.formador} answers={answers} setAnswers={setAnswers} />

        <div className="p-5 border-b border-[#E6E1D6]">
          <label className="block text-sm font-semibold text-[#0F1829] mb-2">
            Sugestões / Críticas <span className="text-[11px] font-normal text-[#6B7280]">(opcional)</span>
          </label>
          <p className="text-[11px] text-[#6B7280] mb-2">
            Temas a desenvolver com maior profundidade, aspectos mais conseguidos e a melhorar, outras observações.
          </p>
          <textarea
            value={sugestoes}
            onChange={(e) => setSugestoes(e.target.value)}
            rows={4}
            maxLength={4000}
            data-testid="avaliacao-sugestoes"
            className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:outline-none focus:ring-2 focus:ring-[#0F1829]/20"
          />
          <p className="text-[10px] text-[#978D78] text-right mt-1">{sugestoes.length} / 4000</p>
        </div>

        <footer className="p-5 flex items-center justify-between gap-3">
          <p className="text-[11px] text-[#6B7280]">
            {allAnswered ? "✓ Todas as perguntas respondidas." : "Por favor responda a todas as perguntas obrigatórias."}
          </p>
          <button
            type="submit"
            disabled={!allAnswered || submitting}
            data-testid="avaliacao-submit"
            className="px-5 py-2 bg-[#0F1829] text-white rounded text-sm font-semibold hover:bg-[#1A2540] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="animate-spin" size={14} /> : null}
            {submitting ? "A enviar..." : "Submeter avaliação"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Section({ title, subtitle, questions, answers, setAnswers }) {
  return (
    <section className="p-5 border-b border-[#E6E1D6]" data-testid={`avaliacao-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-[#0F1829] mb-1">{title}</h2>
      {subtitle && <p className="text-[11px] text-[#6B7280] mb-3">{subtitle}</p>}
      <ul className="space-y-4">
        {questions.map((q) => (
          <ScaleRow
            key={q.key}
            q={q}
            value={answers[q.key]}
            onChange={(v) => setAnswers((prev) => ({ ...prev, [q.key]: v }))}
          />
        ))}
      </ul>
    </section>
  );
}

function ScaleRow({ q, value, onChange }) {
  return (
    <li className="border border-[#F3F0E7] rounded p-3" data-testid={`avaliacao-q-${q.key}`}>
      <p className="text-sm font-medium text-[#0F1829] mb-2">{q.label} <span className="text-[#9B1C1C]">*</span></p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-[#978D78] min-w-[80px]">{q.low}</span>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n;
          return (
            <button
              type="button"
              key={n}
              onClick={() => onChange(n)}
              data-testid={`avaliacao-${q.key}-${n}`}
              className={`w-9 h-9 rounded-full border-2 text-sm font-bold transition ${
                active
                  ? "bg-[#0F1829] text-white border-[#0F1829]"
                  : "bg-white text-[#6B7280] border-[#E6E1D6] hover:border-[#0F1829]"
              }`}
              aria-label={`${q.label}: ${n}`}
            >
              {n}
            </button>
          );
        })}
        <span className="text-[10px] uppercase tracking-wider text-[#978D78] min-w-[100px] text-right">{q.high}</span>
      </div>
    </li>
  );
}

function CenterCard({ tone, title, subtitle }) {
  const toneCfg = {
    ok:    { Icon: CheckCircle2,    bg: "bg-[#E6F4EA]", border: "border-[#A6DFB6]", text: "text-[#0A6B2E]" },
    warn:  { Icon: AlertTriangle,   bg: "bg-[#FFF7E6]", border: "border-[#F0D8A8]", text: "text-[#7A5C18]" },
    error: { Icon: AlertTriangle,   bg: "bg-[#FDE8E8]", border: "border-[#F5A5A5]", text: "text-[#9B1C1C]" },
  }[tone] || { Icon: AlertTriangle, bg: "bg-white", border: "border-[#E6E1D6]", text: "text-[#0F1829]" };
  const { Icon } = toneCfg;
  return (
    <div className="min-h-screen bg-[#FAF8F2] flex items-center justify-center p-4">
      <div className={`max-w-md w-full p-6 ${toneCfg.bg} border ${toneCfg.border} rounded-md`} data-testid="curso-avaliacao-info">
        <Icon className={`mb-3 ${toneCfg.text}`} size={28} />
        <h1 className={`text-lg font-bold ${toneCfg.text}`}>{title}</h1>
        <p className="text-sm mt-2 text-[#0F1829]">{subtitle}</p>
      </div>
    </div>
  );
}
